import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { FileText, Plus, Trash2, Pencil, ExternalLink } from 'lucide-react';
import { AppShell } from '../../components/AppShell';
import { Card } from '../../components/ui/Card';
import { AiInsightCard } from '../../components/ui/AiInsightCard';
import { PageSignalsLink } from '../../components/PageSignalsLink';
import { Pill } from '../../components/ui/Pill';
import { HankoStamp } from '../../components/ui/HankoStamp';
import { useConfirm } from '../../components/ui/ConfirmDialog';
import { api } from '../../lib/api';
import { formatDate } from '../../lib/format';
import { useCan } from '../../hooks/useCan';

type TargetKind = 'ap_invoice' | 'ar_invoice' | 'expense' | 'payment_run' | 'contract' | 'other';
type StepKind = 'user' | 'role' | 'manager';
interface RouteStep { kind: StepKind; value: string; label: string }
interface ApprovalRoute { id: string; name: string; target_kind: TargetKind; steps: RouteStep[] | null }
const TARGET_KINDS: TargetKind[] = ['ap_invoice', 'ar_invoice', 'expense', 'payment_run', 'contract', 'other'];

function RoutesCard({ wsId }: { wsId: string }) {
  const { t } = useTranslation();
  const { canMutate } = useCan();
  const confirm = useConfirm();
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [name, setName] = useState('');
  const [targetKind, setTargetKind] = useState<TargetKind>('ap_invoice');
  const [steps, setSteps] = useState<RouteStep[]>([{ kind: 'role', value: '', label: '' }]);
  const [error, setError] = useState<string | null>(null);

  const routes = useQuery<ApprovalRoute[]>({
    queryKey: ['approval-routes', wsId],
    queryFn: async () => (await api.get<ApprovalRoute[]>(`/workspaces/${wsId}/approvals/routes`)).data,
  });
  const resetForm = () => { setEditingId(null); setName(''); setTargetKind('ap_invoice'); setSteps([{ kind: 'role', value: '', label: '' }]); setError(null); };
  const create = useMutation({
    mutationFn: async () => {
      const body = { name, targetKind, steps };
      return editingId
        ? (await api.patch(`/workspaces/${wsId}/approvals/routes/${editingId}`, body)).data
        : (await api.post(`/workspaces/${wsId}/approvals/routes`, body)).data;
    },
    onSuccess: () => { setOpen(false); resetForm(); qc.invalidateQueries({ queryKey: ['approval-routes', wsId] }); },
    onError: (err: any) => setError(err?.response?.data?.message ?? err?.message ?? 'Failed'),
  });
  const del = useMutation({
    mutationFn: async (id: string) => (await api.delete(`/workspaces/${wsId}/approvals/routes/${id}`)).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['approval-routes', wsId] }),
    onError: (err: any) => setError(err?.response?.data?.message ?? err?.message ?? 'Failed'),
  });
  const inputCn = 'px-2 py-1.5 text-sm rounded-md border border-ink-300 dark:border-ink-700 bg-white dark:bg-ink-900';
  const setStep = (i: number, patch: Partial<RouteStep>) => setSteps(steps.map((s, idx) => (idx === i ? { ...s, ...patch } : s)));
  const submit = () => {
    setError(null);
    if (!name.trim()) return setError(t('approvals.routeNameRequired'));
    if (!steps.some((s) => s.value.trim())) return setError(t('approvals.routeStepRequired'));
    create.mutate();
  };

  return (
    <Card className="mb-5">
      <div className="px-5 py-3 border-b border-ink-200 dark:border-ink-800 flex items-center justify-between">
        <span className="font-medium">{t('approvals.routes')}</span>
        {canMutate && (
          <button type="button" onClick={() => { resetForm(); setOpen((o) => !o); }} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-accent-600 text-white text-xs font-medium hover:bg-accent-700">
            <Plus size={12} /> {t('approvals.newRoute')}
          </button>
        )}
      </div>
      {open && (
        <div className="p-4 border-b border-ink-200 dark:border-ink-800 bg-ink-50 dark:bg-ink-900/40 space-y-3">
          <div className="grid sm:grid-cols-2 gap-3">
            <input value={name} onChange={(e) => setName(e.target.value)} placeholder={t('approvals.routeName')} className={inputCn} />
            <select value={targetKind} onChange={(e) => setTargetKind(e.target.value as TargetKind)} className={inputCn}>
              {TARGET_KINDS.map((k) => <option key={k} value={k}>{t(`approvals.targetKinds.${k}`)}</option>)}
            </select>
          </div>
          <div className="space-y-2">
            {steps.map((s, i) => (
              <div key={i} className="flex items-center gap-2">
                <select value={s.kind} onChange={(e) => setStep(i, { kind: e.target.value as StepKind })} className={`${inputCn} w-28`}>
                  <option value="role">{t('approvals.stepKind.role')}</option>
                  <option value="user">{t('approvals.stepKind.user')}</option>
                  <option value="manager">{t('approvals.stepKind.manager')}</option>
                </select>
                <input value={s.value} onChange={(e) => setStep(i, { value: e.target.value })} placeholder={t('approvals.stepValue')} className={`${inputCn} flex-1`} />
                <input value={s.label} onChange={(e) => setStep(i, { label: e.target.value })} placeholder={t('approvals.stepLabel')} className={`${inputCn} flex-1`} />
                <button type="button" onClick={() => setSteps(steps.filter((_, idx) => idx !== i))} className="p-1.5 text-ink-400 hover:text-warn-600"><Trash2 size={14} /></button>
              </div>
            ))}
            <button type="button" onClick={() => setSteps([...steps, { kind: 'role', value: '', label: '' }])} className="text-xs text-accent-600 hover:text-accent-700 inline-flex items-center gap-1">
              <Plus size={12} /> {t('approvals.addStep')}
            </button>
          </div>
          {error && <div className="text-xs text-warn-600">{error}</div>}
          <button type="button" onClick={submit} disabled={create.isPending} className="px-3 py-1.5 rounded-lg bg-accent-600 text-white text-xs font-medium hover:bg-accent-700 disabled:opacity-50">
            {t('common.save')}
          </button>
        </div>
      )}
      {routes.data && routes.data.length > 0 ? (
        <ul className="divide-y divide-ink-200 dark:divide-ink-800">
          {routes.data.map((r) => (
            <li key={r.id} className="px-5 py-3 flex items-center justify-between text-sm">
              <div>
                <div className="font-medium">{r.name}</div>
                <div className="text-xs text-ink-500">{(r.steps ?? []).map((s) => s.label || s.value).join(' → ')}</div>
              </div>
              <div className="flex items-center gap-2">
                <Pill tone="neutral">{t(`approvals.targetKinds.${r.target_kind}`)}</Pill>
                {canMutate && (
                  <>
                    <button
                      type="button"
                      onClick={() => { setEditingId(r.id); setName(r.name); setTargetKind(r.target_kind); setSteps(r.steps?.length ? r.steps : [{ kind: 'role', value: '', label: '' }]); setOpen(true); }}
                      className="p-1.5 rounded-md text-ink-400 hover:text-accent-600"
                      title={t('common.edit')}
                    >
                      <Pencil size={14} />
                    </button>
                    <button
                      type="button"
                      onClick={async () => { if (await confirm({ title: t('common.confirmDelete'), confirmLabel: t('common.delete'), destructive: true })) del.mutate(r.id); }}
                      className="p-1.5 rounded-md text-ink-400 hover:text-warn-600"
                      title={t('common.delete')}
                    >
                      <Trash2 size={14} />
                    </button>
                  </>
                )}
              </div>
            </li>
          ))}
        </ul>
      ) : (
        <div className="px-5 py-4 text-xs text-ink-500">{t('approvals.noRoutes')}</div>
      )}
    </Card>
  );
}

/** Stream the 稟議書 PDF for an approval and open it in a new tab. */
async function openRingiPdf(wsId: string, id: string): Promise<void> {
  const r = await api.get(`/workspaces/${wsId}/approvals/${id}/ringi.pdf`, { responseType: 'blob' });
  const url = URL.createObjectURL(r.data as Blob);
  window.open(url, '_blank', 'noopener,noreferrer');
  setTimeout(() => URL.revokeObjectURL(url), 60_000);
}

interface PendingApproval {
  id: string;
  route_id: string | null;
  target_kind: string;
  target_id: string;
  status: string;
  current_step: number;
  requested_at: string;
  steps: Array<{ kind: string; value: string; label: string }> | null;
}

/**
 * Map an approval's target (`target_kind` + `target_id`) to the deep link that
 * opens the underlying record. AP/AR invoices open their drawer via `?open=`;
 * expenses/payments land on their list page. Returns `null` for kinds we cannot
 * deep-link (e.g. `other`) so the affordance is hidden rather than dead.
 */
function sourcePath(wsId: string, kind: string, id: string): string | null {
  const base = `/app/workspace/${wsId}`;
  switch (kind) {
    case 'ap_invoice':
      return `${base}/ap-invoices?open=${id}`;
    case 'ar_invoice':
      return `${base}/ar-invoices?open=${id}`;
    case 'expense':
      return `${base}/expenses`;
    case 'payment_run':
      return `${base}/payments`;
    case 'contract':
      return `${base}/digital-records`;
    default:
      return null;
  }
}

export function ApprovalsPage() {
  const { wsId } = useParams<{ wsId: string }>();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const { canMutate } = useCan();
  const qc = useQueryClient();
  const pending = useQuery<PendingApproval[]>({
    queryKey: ['approvals', wsId, 'pending'],
    enabled: !!wsId,
    queryFn: async () => (await api.get(`/workspaces/${wsId}/approvals/pending`)).data,
  });

  const [decideError, setDecideError] = useState<string | null>(null);
  const decide = useMutation({
    mutationFn: async (args: { id: string; decision: 'approve' | 'reject' }) =>
      (await api.post(`/workspaces/${wsId}/approvals/${args.id}/decide`, { decision: args.decision })).data,
    onSuccess: () => { setDecideError(null); qc.invalidateQueries({ queryKey: ['approvals', wsId, 'pending'] }); },
    onError: (err: any) => setDecideError(err?.response?.data?.message ?? err?.message ?? 'Decision failed'),
  });

  return (
    <AppShell>
      <div className="px-8 py-7 max-w-7xl">
        <div className="flex items-start justify-between gap-3 mb-2">
          <h1 className="display text-2xl font-semibold">{t('approvals.title')}</h1>
          <PageSignalsLink kinds={['pending_approval']} />
        </div>
        <p className="text-sm text-ink-500 mb-6">{t('approvals.subtitle')}</p>
        {wsId && <RoutesCard wsId={wsId} />}
        <div className="mb-5">
          <AiInsightCard
            agent="anomaly"
            title={t('aiOutput.cards.approvals')}
            prompt={t('aiOutput.prompts.approvals')}
          />
        </div>
        {decideError && (
          <div className="mb-4 text-sm text-warn-600 rounded-lg border border-warn-300 bg-warn-50 dark:bg-warn-900/20 px-3 py-2">
            {decideError}
          </div>
        )}
        <Card>
          {pending.isLoading ? (
            <div className="p-10 text-center text-ink-500">{t('common.loading')}</div>
          ) : !pending.data || pending.data.length === 0 ? (
            <div className="p-10 text-center text-ink-500">{t('approvals.empty')}</div>
          ) : (
            <ul className="divide-y divide-ink-200 dark:divide-ink-800">
              {pending.data.map((a) => (
                <li key={a.id} className="p-5">
                  <div className="flex items-start justify-between mb-3 gap-3">
                    <div className="min-w-0">
                      <div className="text-xs text-ink-500">{t(`approvals.targetKinds.${a.target_kind}`, a.target_kind)}</div>
                      <div className="font-mono text-xs text-ink-400 mt-0.5 truncate">{a.target_id}</div>
                    </div>
                    <div className="flex items-center gap-3 shrink-0">
                      {(() => {
                        const path = wsId ? sourcePath(wsId, a.target_kind, a.target_id) : null;
                        if (!path) return null;
                        return (
                          <button
                            type="button"
                            onClick={() => navigate(path)}
                            className="inline-flex items-center gap-1.5 text-xs font-medium text-accent-600 hover:text-accent-700"
                          >
                            <ExternalLink size={12} />
                            {t('common.viewSource')}
                          </button>
                        );
                      })()}
                      <div className="text-xs text-ink-500 num">{formatDate(a.requested_at, i18n.language)}</div>
                    </div>
                  </div>
                  {a.steps && (
                    <div className="flex items-center gap-2 mb-3 overflow-x-auto pb-2">
                      {a.steps.map((s, i) => (
                        <div key={i} className="flex items-center gap-2 shrink-0">
                          <div className={`flex flex-col items-center gap-1 ${i === a.current_step ? '' : 'opacity-50'}`}>
                            <HankoStamp text={s.label.slice(0, 2)} size={40} animate={i === a.current_step} />
                            <div className="text-[10px] text-ink-500">{s.label}</div>
                          </div>
                          {i < (a.steps?.length ?? 0) - 1 && <span className="text-ink-300">→</span>}
                        </div>
                      ))}
                    </div>
                  )}
                  <div className="flex gap-2">
                    <button
                      onClick={() => decide.mutate({ id: a.id, decision: 'approve' })}
                      disabled={decide.isPending || !canMutate}
                      title={canMutate ? undefined : t('common.viewerReadOnly')}
                      className="px-3 py-1.5 rounded-lg bg-accent-600 text-white text-xs font-medium hover:bg-accent-700 disabled:opacity-50"
                    >
                      {t('approvals.approveAndStamp')}
                    </button>
                    <button
                      onClick={() => decide.mutate({ id: a.id, decision: 'reject' })}
                      disabled={decide.isPending || !canMutate}
                      title={canMutate ? undefined : t('common.viewerReadOnly')}
                      className="px-3 py-1.5 rounded-lg border border-ink-300 dark:border-ink-700 text-xs font-medium disabled:opacity-50"
                    >
                      {t('common.reject')}
                    </button>
                    <button
                      onClick={() => openRingiPdf(wsId!, a.id)}
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-ink-300 dark:border-ink-700 text-xs font-medium hover:bg-ink-50 dark:hover:bg-ink-900"
                    >
                      <FileText size={12} />
                      {t('approvals.ringiPdf')}
                    </button>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </Card>
      </div>
    </AppShell>
  );
}
